
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { ICommunity, IModule, ISubmodule } from '@/model/Community/communityInterfaces';
import { IErrors } from '@/model/IErrors';
import CommunityNav from '@/components/community/widget/CommunityNav.vue';
import NotificationTemplatesPanel from '@/components/community/modules/NotificationTemplatesPanel.vue';
import { moduleOptionsLabel } from '@/components/community/modules/moduleOptions';
import {
  INTEGRATION_COMMUNICATION_SLUGS,
  MODULE_SLUG_INTEGRATIONS,
  MODULE_SLUG_NOTIFICATIONS,
  NOTIFICATION_CHANNEL_SLUGS,
  NOTIFICATION_CUSTOM_TEMPLATES_SLUG,
} from '@/model/Community/communityConstants';

const communityModule = namespace('communityModule');

type NotificationMode = 'regular' | 'custom';

@Component({
  name: 'CommunityModule',
  components: { BoPanel, CommunityNav, NotificationTemplatesPanel },
})
export default class CommunityModule extends Vue {
  @Prop()
  protected communityId!: number;

  @Prop()
  protected moduleSlug!: string;

  @communityModule.Getter('community')
  protected community!: ICommunity;

  @communityModule.Getter('availableModules')
  protected availableModules!: IModule[];

  @communityModule.Getter('errors')
  protected errors!: IErrors;

  @communityModule.Action('fetchCommunity')
  private fetchCommunity!: (id: number) => void;

  @communityModule.Action('reloadModules')
  private reloadModules!: () => Promise<void>;

  @communityModule.Action('updateCommunity')
  private updateCommunity!: (payload: { communityId: number; data: { submodule_ids: number[] } }) => void;

  protected loading: boolean = true;
  protected modulesLoaded: boolean = false;
  protected saving: boolean = false;
  protected selectedIds: number[] = [];
  private savedIds: number[] = [];
  protected notificationMode: NotificationMode | null = null;
  private subscribes: IOnOffHandlers = {};

  get module(): IModule | undefined {
    return this.availableModules.find((mod: IModule) => mod.slug === this.moduleSlug);
  }

  get isEnabled(): boolean {
    return !!this.module && !!this.community && (this.community.modules || [])
      .some((enabled: IModule) => enabled.id === (this.module as IModule).id);
  }

  get hasOptions(): boolean {
    return !!this.module && this.isEnabled && !!this.module.submodules && this.module.submodules.length > 0;
  }

  get optionsLabel(): string {
    return moduleOptionsLabel(this.moduleSlug);
  }

  get submodules(): ISubmodule[] {
    return this.module && this.module.submodules ? this.module.submodules : [];
  }

  get singleChoices(): Array<{ id: number | null; name: string; description: string | null }> {
    const none = { id: null, name: this.$t('communities.configuration.none') as string, description: this.$t('communities.configuration.noneHelp') as string };
    return [none, ...this.submodules.map((submodule) => ({ id: submodule.id as number | null, name: submodule.name, description: submodule.description }))];
  }

  get singleSelectedId(): number | null {
    return this.selectedIds.length ? this.selectedIds[0] : null;
  }

  selectSingle(id: number | null) {
    this.selectedIds = id === null ? [] : [id];
  }

  moveSingle(index: number, step: number) {
    const next = (index + step + this.singleChoices.length) % this.singleChoices.length;
    this.selectSingle(this.singleChoices[next].id);
    this.focusRef('choice', next);
  }

  get isIntegrations(): boolean {
    return this.moduleSlug === MODULE_SLUG_INTEGRATIONS;
  }

  get integrationTypes(): ISubmodule[] {
    return this.submodules.filter((submodule) => INTEGRATION_COMMUNICATION_SLUGS.indexOf(submodule.slug) === -1);
  }

  get communicationMethods(): ISubmodule[] {
    return this.submodules.filter((submodule) => INTEGRATION_COMMUNICATION_SLUGS.indexOf(submodule.slug) !== -1);
  }

  get selectedIntegrationTypeId(): number | null {
    const integrationType = this.integrationTypes.find((each) => this.isSelected(each.id));
    return integrationType ? integrationType.id : null;
  }

  selectIntegrationType(id: number) {
    const integrationTypeIds = this.integrationTypes.map((integrationType) => integrationType.id);
    this.selectedIds = [...this.selectedIds.filter((selected) => integrationTypeIds.indexOf(selected) === -1), id];
  }

  moveIntegrationType(index: number, step: number) {
    const next = (index + step + this.integrationTypes.length) % this.integrationTypes.length;
    this.selectIntegrationType(this.integrationTypes[next].id);
    this.focusRef('choice', next);
  }

  get isNotifications(): boolean {
    return this.moduleSlug === MODULE_SLUG_NOTIFICATIONS;
  }

  get channels(): ISubmodule[] {
    return this.submodules.filter((submodule) => NOTIFICATION_CHANNEL_SLUGS.indexOf(submodule.slug) !== -1);
  }

  get customTemplates(): ISubmodule | undefined {
    return this.submodules.find((submodule) => submodule.slug === NOTIFICATION_CUSTOM_TEMPLATES_SLUG);
  }

  get selectedChannelCount(): number {
    return this.channels.filter((channel) => this.isSelected(channel.id)).length;
  }

  get customDisabledReason(): string | null {
    return this.customTemplates ? null : this.$t('communities.configuration.noCustomTemplates') as string;
  }

  get notificationModes(): Array<{ value: NotificationMode; name: string; description: string; disabledReason: string | null }> {
    return [
      { value: 'regular', name: this.$t('communities.configuration.regular') as string, description: this.$t('communities.configuration.regularHelp') as string, disabledReason: null },
      { value: 'custom', name: this.$t('communities.configuration.custom') as string, description: this.$t('communities.configuration.customHelp') as string, disabledReason: this.customDisabledReason },
    ];
  }

  selectMode(mode: NotificationMode) {
    if (mode === 'custom' && this.customDisabledReason) return;
    this.notificationMode = mode;
    const templatesId = this.customTemplates ? this.customTemplates.id : null;
    const withoutTemplates = this.selectedIds.filter((id) => id !== templatesId);
    this.selectedIds = mode === 'custom' && templatesId !== null ? [...withoutTemplates, templatesId] : withoutTemplates;
  }

  moveMode(index: number, step: number) {
    const modes = this.notificationModes.filter((mode) => !mode.disabledReason);
    const current = modes.findIndex((mode) => mode.value === this.notificationModes[index].value);
    const next = modes[(current + step + modes.length) % modes.length];
    this.selectMode(next.value);
    this.focusRef('mode', this.notificationModes.findIndex((mode) => mode.value === next.value));
  }

  isSelected(id: number): boolean {
    return this.selectedIds.indexOf(id) !== -1;
  }

  toggle(id: number) {
    this.selectedIds = this.isSelected(id)
      ? this.selectedIds.filter((selected: number) => selected !== id)
      : [...this.selectedIds, id];
  }

  get valid(): boolean {
    if (this.isIntegrations) return !this.integrationTypes.length || this.selectedIntegrationTypeId !== null;
    if (this.isNotifications) return !this.notificationMode || this.selectedChannelCount > 0;
    return true;
  }

  private focusRef(name: string, index: number) {
    this.$nextTick(() => {
      const buttons = this.$refs[name] as HTMLElement[] | undefined;
      if (buttons && buttons[index]) buttons[index].focus();
    });
  }

  get dirty(): boolean {
    return JSON.stringify([...this.selectedIds].sort()) !== JSON.stringify([...this.savedIds].sort());
  }

  get submoduleErrors(): string[] {
    return Object.keys(this.errors || {})
      .filter((field: string) => field === 'submodule_ids' || field.indexOf('submodule_ids.') === 0)
      .reduce((messages: string[], field: string) => messages.concat(this.errors[field]), []);
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.communityLoaded': this.initSelection,
      'backend.call.begin.communityUpdated': () => { this.saving = true; },
      'backend.call.OK.communityUpdated': this.onSaveSuccess,
      'backend.call.KO.communityUpdated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.reloadModules()
      .catch(() => undefined)
      .finally(() => {
        this.modulesLoaded = true;
        this.initSelection();
      });
    this.fetchCommunity(this.communityId);
  }

  @Watch('moduleSlug')
  onModuleChange() {
    this.initSelection();
  }

  initSelection() {
    const mod = this.module;
    this.selectedIds = mod && this.community && this.community.submodules
      ? this.community.submodules.filter((submodule) => submodule.module_id === mod.id).map((submodule) => submodule.id)
      : [];
    this.savedIds = [...this.selectedIds];
    const usesTemplates = !!this.customTemplates && this.isSelected(this.customTemplates.id);
    this.notificationMode = usesTemplates ? 'custom' : (this.selectedChannelCount ? 'regular' : null);
  }

  onSave() {
    const mod = this.module as IModule;
    const others = (this.community.submodules || [])
      .filter((submodule) => submodule.module_id !== mod.id)
      .map((submodule) => submodule.id);
    const ids = [...others, ...this.selectedIds];
    this.updateCommunity({ communityId: this.communityId, data: { submodule_ids: ids } });
  }

  private onSaveSuccess() {
    this.saving = false;
    this.initSelection();
    this.$uiBus.emit('success', this.$t('common.saved'));
  }
}
